import React, { memo, useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

// Components
import NewOrderHeader from "./NewOrderHeader";
import CustomerInfoPanel from "./customer";
import OrderTypeSelector from "./orderType";
import Cart from "./cart";
import Products from "./products";
import ProductSpecs from "./products/ProductSpecs";
import PaymentInfoPanel from "./financial/PaymentInfoPanel";
import FormFinance from "./financial/FormFinance";

// New Windows 11 Components
import CommandBar from "./CommandBar";
import CartDrawer from "./cart/CartDrawer";
import TableInfoBanner from "./TableInfoBanner";
import CustomerFormModal from "./customer/CustomerFormModal";
import AddressFormModal from "./address/AddressFormModal";

// Icons
import { ShoppingCart } from "@mui/icons-material";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import PeopleIcon from "@mui/icons-material/People";
import ErrorIcon from "@mui/icons-material/Error";

// Theme
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { blue } from "@mui/material/colors";

// Actions
import {
  resetNewOrderSlice,
  setAddress,
  setExpandGrids,
  setNewOrderId,
  setOrderType,
  setServiceFee,
  setShowAddressForm,
  setShowCartDrawer,
  updateOrderValues,
} from "../../../store/actions/newOrderSlice";

// API
import { getDeliveryCenterServiceFee } from "../../../api/DeliveryCenter/deliveryCenter";
import {
  createNewOrder,
  createNewSaloonTableOrder,
  orderPoll,
} from "../../../api/order";
import { setShowOrderInfosModal } from "../../../store/actions/ordersSlice";
import { dispatchSnackBarMessage } from "../../../utils/snackbar";
import api from "../../../store/services/api";

// Order creation phrases
import FoodLoadingAnimation from "../../indicators/FoodLoadingAnimation";
import { isAddressValid } from "../../../common/validators";
import Alert from "@mui/material/Alert";

const NewOrder = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();

  // Get table info from route state if available
  const tableInfo = location.state?.tableInfo || null;

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [poolingOrder, setPoolingOrder] = useState(false);
  const [addressError, setAddressError] = useState(false);

  const newOrderProduct = useSelector((state) => state.newOrder.product);

  const address = useSelector((state) => state.newOrder.address);
  const customer = useSelector((state) => state.newOrder.customer);
  const orderType = useSelector((state) => state.newOrder.orderType);
  const itemsTotal = useSelector((state) => state.newOrder.itemsTotal);
  const total = useSelector((state) => state.newOrder.total);
  const discountsValue = useSelector((state) => state.newOrder.discountsValue);
  const paymentMethods = useSelector((state) => state.newOrder.paymentMethods);
  const serviceFeeAmount = useSelector(
    (state) => state.newOrder.serviceFeeAmount
  );
  const cartProducts = useSelector((state) => state.newOrder.cartProducts);
  const applyServiceFee = useSelector(
    (state) => state.newOrder.applyServiceFee
  );
  const serviceFee = useSelector((state) => state.newOrder.serviceFee);
  const discounts = useSelector((state) => state.newOrder.discounts);
  const expandGrids = useSelector((state) => state.newOrder.expandGrids);

  const newOrderTable = useSelector((state) => state.orders.newOrderTable);

  const showAddressForm = useSelector(
    (state) => state.newOrder.showAddressForm
  );

  const showCustomerForm = useSelector(
    (state) => state.newOrder.showCustomerForm
  );

  const showPaymentForm = useSelector(
    (state) => state.newOrder.showPaymentForm
  );

  const deliveryTax = useSelector((state) => state.newOrder.address?.fee);

  // Set orderType to INDOOR if tableInfo is available
  useEffect(() => {
    if (tableInfo) {
      dispatch(setOrderType("INDOOR"));
    }
  }, [tableInfo, dispatch]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        const params = {
          type: tableInfo || newOrderTable ? "saloon" : "counter",
        };
        const { data } = await getDeliveryCenterServiceFee(params);
        dispatch(setServiceFee(data?.service_fee || 0));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    if (!customer || customer.ignoreAddCustomer) return;

    const fetchAddress = async () => {
      try {
        const { data } = await api.get(
          `/addresses/?cid=${customer.customer_id}`
        );

        if (data) {
          const addressObj = {
            address_id: uuidv4(),
            street: data.street,
            neighborhood: data.neighborhood,
            house_number: data.house_number,
            address_complement: data.address_complement,
            address_reference: data.address_reference,
            cep: data.cep,
            city: data.city,
            uf: data.uf,
            lat: parseFloat(data.lat),
            long: parseFloat(data.long),
            fee: null,
            distance: null,
          };

          await dispatch(setAddress(addressObj));
          if (!tableInfo) {
            await dispatch(setOrderType("DELIVERY"));
          }
          await dispatch(setShowAddressForm(true));
        }
      } catch (e) {
        console.error("Error fetching address:", e);
      }
    };

    fetchAddress();
  }, [customer, dispatch, tableInfo]);

  useEffect(() => {
    dispatch(
      setExpandGrids(showAddressForm || showCustomerForm || showPaymentForm)
    );
  }, [showAddressForm, showCustomerForm, showPaymentForm, dispatch]);

  useEffect(() => {
    // 1. Calculate itemsTotal from cartProducts by summing total_price of each product
    const newItemsTotal = cartProducts.reduce(
      (acc, product) => acc + product.total_price * product.quantity,
      0
    );

    // 3. Compute discount value (treat discounts as an array)
    const discountsArray = Array.isArray(discounts) ? discounts : [discounts];
    const computedDiscountValue = discountsArray.reduce(
      (acc, discount) => acc + discount.value,
      0
    );

    const parsedDeliveryTax = parseFloat(
      parseFloat(deliveryTax || 0).toFixed(2)
    );

    // 4. Calculate base total: items total plus fee, minus discount
    const baseTotal = newItemsTotal + parsedDeliveryTax - computedDiscountValue;

    // 5. Calculate service fee amount if applicable
    const computedServiceFeeAmount = applyServiceFee
      ? calculateServiceFeeAmount(serviceFee, baseTotal)
      : 0;

    // 6. Set final total and total to pay
    const finalTotal = baseTotal + computedServiceFeeAmount;

    const newValues = {
      total: finalTotal,
      totalToPay: finalTotal,
      itemsTotal: newItemsTotal,
      discountsValue: computedDiscountValue,
      serviceFeeAmount: computedServiceFeeAmount,
    };

    dispatch(updateOrderValues(newValues));
  }, [
    cartProducts,
    address,
    discounts,
    applyServiceFee,
    serviceFee,
    deliveryTax,
    dispatch,
  ]);

  const calculateServiceFeeAmount = useCallback(
    (percentage, total) => {
      if (!applyServiceFee || !percentage || percentage === 0) {
        return 0;
      }
      return (percentage / 100) * total;
    },
    [applyServiceFee]
  );

  const handleDrawerToggle = useCallback(() => {
    setIsDrawerOpen(!isDrawerOpen);
  }, [isDrawerOpen]);

  const submitNewOrder = useCallback(async () => {
    if (orderType === "DELIVERY") {
      if (!isAddressValid(address)) {
        setAddressError(true);
        dispatchSnackBarMessage(
          Date.now(),
          "O endereço precisa de coordenadas (latitude e longitude) para prosseguir com a entrega.",
          "error"
        );
        return;
      }
    }

    setPoolingOrder(true);
    const newOrderId = uuidv4();
    dispatch(setNewOrderId(newOrderId));

    try {
      const newOrderObj = {
        id: newOrderId,
        orderTiming: "IMMEDIATE",
        orderType,
        observation: "",
        customer: {
          id: customer?.shouldCreate ? uuidv4() : customer?.customer_id,
          name: customer?.name,
          documentNumber: customer?.cpf,
          phoneNumber: customer?.phonenmbr,
          ignoreAddCustomer: tableInfo ? true : customer.ignoreAddCustomer,
        },
        items: cartProducts,
        benefits: discounts,
        total: {
          serviceFee: serviceFee,
          serviceFeeAmount: serviceFeeAmount,
          subTotal: itemsTotal,
          deliveryFee: orderType === "DELIVERY" ? address.fee : 0,
          benefits: discountsValue,
          total,
        },
        payments: paymentMethods ?? [],
        ...(orderType === "DELIVERY"
          ? {
              streetName: address.street,
              streetNumber: address.house_number,
              neighborhood: address.neighborhood,
              complement: address.address_complement,
              reference: address.address_reference,
              postalCode: address.cep,
              city: address.city,
              state: address.uf,
              country: "BR",
              coordinates: {
                latitude: address.lat,
                longitude: address.long,
              },
            }
          : {}),
        ...(orderType === "INDOOR"
          ? {
              table: tableInfo || newOrderTable,
              indoor: "TABLE",
              indoor_table: (tableInfo?.index ?? newOrderTable?.index) + 1,
            }
          : {}),
      };

      if (orderType !== "INDOOR") {
        await createNewOrder(newOrderObj);
      } else {
        await createNewSaloonTableOrder(newOrderObj);
      }

      dispatch(resetNewOrderSlice());
      await pollUntilResultIsFound(newOrderId);
    } catch (e) {
      console.log("ERROR", e);
      alert(`Alguma coisa deu errado, entre em contato com o suporte\n ${e}`);
      setPoolingOrder(false);
    }
  }, [
    orderType,
    customer,
    cartProducts,
    discounts,
    serviceFee,
    serviceFeeAmount,
    itemsTotal,
    address,
    discountsValue,
    total,
    paymentMethods,
    tableInfo,
    newOrderTable,
    dispatch,
    history,
  ]);

  const pollUntilResultIsFound = async (id) => {
    const maxRetries = 5;
    let attempts = 0;

    const intervalId = setInterval(async () => {
      try {
        const params = { id: id };
        const response = await orderPoll(params);
        attempts += 1;

        if ("id" in response.data) {
          clearInterval(intervalId);
          setLoading(false);
          setPoolingOrder(false);

          await dispatch(setShowOrderInfosModal({ orderId: response.data.id }));

          history.push("/central-delivery");
        } else if (attempts >= maxRetries) {
          clearInterval(intervalId);
          setLoading(false);
          setPoolingOrder(false);
          alert(
            "Max retries reached. Please try again later or contact support."
          );
        }
      } catch (error) {
        console.error(error);
        if (attempts >= maxRetries) {
          clearInterval(intervalId);
          setLoading(false);
          setPoolingOrder(false);
          dispatchSnackBarMessage(
            Date.now(),
            "Pedido ainda não encontrado",
            "error"
          );
        }
      }
    }, 1000);
  };

  if (poolingOrder) {
    return (
      <Box sx={{ width: "100%", height: "100vh" }}>
        <FoodLoadingAnimation />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        py: 1,
        pl: 6,
        pr: 3,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Address Error Alert */}
      {addressError && orderType === "DELIVERY" && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          icon={<ErrorIcon />}
          onClose={() => setAddressError(false)}
        >
          O endereço precisa ter coordenadas de latitude e longitude para
          prosseguir com a entrega.
        </Alert>
      )}

      {/* Table Info Banner (conditional) */}
      {tableInfo && <TableInfoBanner tableInfo={tableInfo} />}

      {/* Command Bar (when not in overlay forms) */}
      {!newOrderProduct && !showPaymentForm && (
        <CommandBar tableInfo={tableInfo} submitOrder={submitNewOrder} />
      )}

      {/* Main Content (full width) */}
      <Box sx={{ flexGrow: 1, overflow: "auto", mt: 2 }}>
        {newOrderProduct && <ProductSpecs product={newOrderProduct} />}
        {showPaymentForm && <FormFinance onCreateOrder={submitNewOrder} />}
        {!newOrderProduct && !showPaymentForm && <Products />}
      </Box>

      {/* Cart Drawer (desktop + mobile) */}
      <CartDrawer submitOrder={submitNewOrder} />

      {/* Mobile Cart Button (floating) */}
      {isMobile && (
        <IconButton
          onClick={() => dispatch(setShowCartDrawer(true))}
          sx={{
            position: "fixed",
            bottom: 24, // MOVED from top-left to bottom-right
            right: 24,
            bgcolor: "primary.main",
            color: "primary.contrastText",
            "&:hover": {
              bgcolor: "primary.dark",
            },
            width: 64,
            height: 64,
            boxShadow: 3,
            zIndex: 1200,
          }}
        >
          <Badge badgeContent={cartProducts.length} color="error">
            <ShoppingCart />
          </Badge>
        </IconButton>
      )}

      {/* Form Modals */}
      <CustomerFormModal />
      <AddressFormModal />
    </Box>
  );
};

export default NewOrder;
